import { motion } from 'motion/react';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

/**
 * Loading Spinner - Basic loading animation
 */
interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function LoadingSpinner({ size = 'md', className }: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'h-4 w-4 border-2',
    md: 'h-8 w-8 border-3',
    lg: 'h-12 w-12 border-4',
  };

  return (
    <div
      className={cn(
        'animate-spin rounded-full border-indigo-200 border-t-indigo-600',
        sizeClasses[size],
        className
      )}
    />
  );
}

/**
 * Loading Dots - Bouncing dots animation
 */
export function LoadingDots({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center gap-1', className)}>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="h-2 w-2 rounded-full bg-indigo-500"
          animate={{
            y: [0, -8, 0],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 0.6,
            repeat: Infinity,
            delay: i * 0.15,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}

/**
 * Loading Page - Full page loading state
 */
interface LoadingPageProps {
  message?: string;
  showEmoji?: boolean;
}

export function LoadingPage({ message, showEmoji = true }: LoadingPageProps) {
  const { t } = useTranslation();
  const displayMessage = message ?? t('common:loading');
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        {/* Brand Icon with Pulse Animation */}
        {showEmoji && (
          <motion.div
            className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 shadow-lg shadow-violet-500/30"
            animate={{
              scale: [1, 1.05, 1],
              opacity: [1, 0.9, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <Sparkles className="h-8 w-8 text-white" />
          </motion.div>
        )}

        {/* Loading Dots */}
        <div className="mb-4 flex justify-center">
          <LoadingDots />
        </div>

        {/* Message */}
        <motion.p
          className="text-gray-600"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          {displayMessage}
        </motion.p>
      </motion.div>
    </div>
  );
}

/**
 * Loading Card - Card skeleton
 */
export function LoadingCard() {
  return (
    <div className="rounded-xl border bg-white p-6 shadow-lg">
      <div className="animate-pulse space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="h-8 w-16 rounded-full bg-gray-200" />
          <div className="h-6 w-32 rounded bg-gray-200" />
        </div>

        {/* Title */}
        <div className="h-8 w-3/4 rounded bg-gray-200" />

        {/* Content lines */}
        <div className="space-y-2">
          <div className="h-4 w-full rounded bg-gray-200" />
          <div className="h-4 w-5/6 rounded bg-gray-200" />
          <div className="h-4 w-4/6 rounded bg-gray-200" />
        </div>

        {/* Button */}
        <div className="h-12 w-full rounded-lg bg-gray-200" />
      </div>
    </div>
  );
}

/**
 * Loading Task - Task loading page (beautified version)
 */
export function LoadingTask() {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg"
      >
        <div className="rounded-xl border bg-white p-8 shadow-xl">
          <div className="animate-pulse space-y-6">
            {/* Badge */}
            <div className="flex justify-center">
              <div className="h-6 w-16 rounded-full bg-indigo-100" />
            </div>

            {/* Title */}
            <div className="space-y-2 text-center">
              <div className="mx-auto h-8 w-2/3 rounded bg-gray-200" />
              <div className="mx-auto h-4 w-1/2 rounded bg-gray-100" />
            </div>

            {/* Icons */}
            <div className="flex justify-center gap-8">
              <div className="text-center">
                <div className="mx-auto mb-2 h-10 w-10 rounded-full bg-gray-100" />
                <div className="mx-auto h-3 w-12 rounded bg-gray-100" />
              </div>
              <div className="text-center">
                <div className="mx-auto mb-2 h-10 w-10 rounded-full bg-gray-100" />
                <div className="mx-auto h-3 w-12 rounded bg-gray-100" />
              </div>
            </div>

            {/* Button */}
            <div className="h-12 w-full rounded-lg bg-indigo-100" />
          </div>

          {/* Loading indicator */}
          <div className="mt-6 flex items-center justify-center gap-2">
            <LoadingSpinner size="sm" />
            <span className="text-sm text-gray-500">{t('common:loadingContent')}</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/**
 * Page Transition Wrapper - Page transition animation wrapper
 */
interface PageTransitionProps {
  children: React.ReactNode;
  className?: string;
}

export function PageTransition({ children, className }: PageTransitionProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
